import React, { useRef, useEffect } from "react";
import SignaturePad from "signature_pad";
import { t } from "../locales/nl";

interface SignatureCanvasProps {
  label: string;
  // eslint-disable-next-line no-unused-vars
  onSignatureChange: (signature: string | null) => void;
  required?: boolean;
  error?: string;
  value?: string | null;
}

const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  label,
  onSignatureChange,
  required,
  error,
  value,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const valueRef = useRef(value);
  // Track whether the latest value change originated from the user drawing
  // on this canvas. When true, skip fromDataURL to avoid a feedback loop
  // that creates unnecessary Image objects and exhausts mobile Safari's
  // image decoder — which then causes photo submission to fail.
  const isOwnChangeRef = useRef(false);
  // Track canvas dimensions to avoid unnecessary resizes
  const canvasDimensionsRef = useRef<{ width: number; height: number } | null>(null);
  // Debounce timer for resize handler
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const signaturePad = new SignaturePad(canvas, {
        backgroundColor: "rgb(255, 255, 255)",
        penColor: "rgb(0, 0, 0)",
      });
      signaturePadRef.current = signaturePad;

      const resizeCanvas = () => {
        if (!canvasRef.current) return;
        
        const canvas = canvasRef.current;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const displayWidth = canvas.offsetWidth;
        const displayHeight = canvas.offsetHeight;
        
        // Check if dimensions actually changed
        const newWidth = displayWidth * ratio;
        const newHeight = displayHeight * ratio;
        if (
          canvasDimensionsRef.current &&
          canvasDimensionsRef.current.width === newWidth &&
          canvasDimensionsRef.current.height === newHeight
        ) {
          return; // No change needed
        }

        // Save current signature data before resize
        const data = signaturePad.toData();
        const isEmpty = signaturePad.isEmpty();

        // Get context and reset transform before resizing
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Reset transform to avoid cumulative scaling
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // Resize canvas
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        // Apply device pixel ratio scaling
        ctx.scale(ratio, ratio);

        // Update dimensions tracking
        canvasDimensionsRef.current = { width: newWidth, height: newHeight };

        // Restore data - prefer fromData over fromDataURL to avoid Image object creation
        if (!isEmpty && data && data.length > 0) {
          signaturePad.fromData(data);
        } else if (valueRef.current) {
          // Only use fromDataURL as fallback when pad is empty
          signaturePad.fromDataURL(valueRef.current);
        }
      };

      // Initial resize
      resizeCanvas();

      // Debounced resize handler
      const handleResize = () => {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        resizeTimeoutRef.current = setTimeout(() => {
          resizeCanvas();
        }, 150); // 150ms debounce
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to handle value changes from external sources (draft load, clear).
  // Skip when the change came from our own handleEndStroke — the canvas
  // already has the correct content and calling fromDataURL would create
  // an unnecessary Image object.
  useEffect(() => {
    if (signaturePadRef.current) {
      if (isOwnChangeRef.current) {
        isOwnChangeRef.current = false;
        return;
      }
      if (value) {
        signaturePadRef.current.fromDataURL(value);
      } else {
        signaturePadRef.current.clear();
      }
    }
  }, [value]);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      onSignatureChange(null);
    }
  };

  const handleEndStroke = () => {
    if (signaturePadRef.current) {
      const isEmpty = signaturePadRef.current.isEmpty();
      if (!isEmpty) {
        isOwnChangeRef.current = true;
        const dataURL = signaturePadRef.current.toDataURL();
        onSignatureChange(dataURL);
      } else {
        isOwnChangeRef.current = true;
        onSignatureChange(null);
      }
    }
  };

  return (
    <div className="form-group">
      <div className="flex justify-between items-center mb-2">
        <label className="form-label">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <button
          type="button"
          onClick={handleClear}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          {t.clear}
        </button>
      </div>
      <div
        className={`border rounded-md ${error ? "border-red-500" : "border-gray-300"} overflow-hidden`}
      >
        <div className="relative w-full h-48">
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            onMouseUp={handleEndStroke}
            onTouchEnd={handleEndStroke}
          />
        </div>
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default SignatureCanvas;
